import type { StorageProvider } from '../storage/StorageProvider';
import { STORAGE_KEYS } from '../storage/StorageProvider';
import type { ModelConfig, ModelManifestEntry } from './ModelConfig';
import { ConfigError } from '../types';

export class UserModelStore {
  constructor(private storage: StorageProvider) {}

  async saveUserModel(
    config: ModelConfig,
    rom: Uint8Array,
    image?: Uint8Array,
  ): Promise<void> {
    const modelId = String(config.hardwareId) + '_' + config.modelName.replace(/[^a-zA-Z0-9_-]/g, '_');
    const configKey = STORAGE_KEYS.userModelConfig(modelId);
    const romKey = STORAGE_KEYS.userModelRom(modelId);
    const imageKey = STORAGE_KEYS.userModelImage(modelId);

    const configForSave = { ...config };
    delete (configForSave as Record<string, unknown>).romData;
    delete (configForSave as Record<string, unknown>).interfaceImage;

    const encoder = new TextEncoder();
    await this.storage.set(configKey, encoder.encode(JSON.stringify(configForSave)));
    await this.storage.set(romKey, rom);
    if (image) {
      await this.storage.set(imageKey, image);
    }
  }

  async listStoredUserModels(): Promise<ModelManifestEntry[]> {
    const keys = await this.storage.list('user_model:');
    const modelIds = new Set<string>();
    for (const key of keys) {
      const match = key.match(/^user_model:(.+):config$/);
      if (match) {
        modelIds.add(match[1]);
      }
    }

    const entries: ModelManifestEntry[] = [];
    for (const modelId of modelIds) {
      const configKey = STORAGE_KEYS.userModelConfig(modelId);
      const data = await this.storage.get(configKey);
      if (!data) continue;

      const decoder = new TextDecoder();
      const config = JSON.parse(decoder.decode(data)) as ModelConfig;

      const imageKey = STORAGE_KEYS.userModelImage(modelId);
      const hasImage = await this.storage.get(imageKey);

      entries.push({
        id: modelId,
        name: config.modelName,
        hardwareGeneration: config.hardwareGeneration,
        romPath: '',
        hasInterfaceImage: hasImage !== null,
      });
    }
    return entries;
  }

  async loadStoredUserModel(modelId: string): Promise<ModelConfig | null> {
    const configKey = STORAGE_KEYS.userModelConfig(modelId);
    const romKey = STORAGE_KEYS.userModelRom(modelId);
    const imageKey = STORAGE_KEYS.userModelImage(modelId);

    const configData = await this.storage.get(configKey);
    if (!configData) return null;

    const romData = await this.storage.get(romKey);
    if (!romData) {
      throw new ConfigError(`ROM data not found for user model: ${modelId}`);
    }

    const decoder = new TextDecoder();
    const config = JSON.parse(decoder.decode(configData)) as ModelConfig;
    config.romData = romData;

    const imageData = await this.storage.get(imageKey);
    if (imageData) {
      const blob = new Blob([imageData.slice().buffer]);
      const bitmap = await createImageBitmap(blob);
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(bitmap, 0, 0);
        config.interfaceImage = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
      }
      bitmap.close();
    }

    return config;
  }

  async deleteStoredUserModel(modelId: string): Promise<void> {
    const configKey = STORAGE_KEYS.userModelConfig(modelId);
    const romKey = STORAGE_KEYS.userModelRom(modelId);
    const imageKey = STORAGE_KEYS.userModelImage(modelId);

    await this.storage.delete(configKey);
    await this.storage.delete(romKey);
    await this.storage.delete(imageKey);
  }
}
